// U-3 — Главная в pending review state (inline вместо отдельного UploadReview).
// Экран проверки распознанного анализа. Живёт на своём адресе `/uploads/:id/review`;
// до HEAL-46a подменял собой Главную через OnboardingRouter.
// Source: docs/specs/sprint-4/stage-4-frontend.md §4.3 + §2.6 + stage-1-contracts.md §2.6/§3.
//
// Sprint 5A.2+ — backend возвращает enriched_biomarkers + summary через
// UploadReviewEnricher. Body перерисован под прототип #s3b:
//   - Beige banner с clipboard иконкой + date sub-card
//   - СВОДКА block с bar chart (В норме / Требуют внимания / Вне нормы / Без оценки)
//   - Биомаркеры сгруппированы по категориям + цветная полоса слева по status

import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import type { AxiosError } from 'axios';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Loader2,
} from 'lucide-react';
import { useConfirmUpload, useReportUpload, useUpdateReview, useUpload } from '@/api/hooks/uploads';
import { capitalizeFirst } from '@/lib/utils';
import { BORDER_LEFT_BY_TIER, reviewDisplayTier, sortByTierWith } from '@/lib/biomarkerStatus';
import { useUploadErrorToast } from '@/api/error-toast';
import { ConfirmUploadSchema, ReportUploadSchema } from '@/api/schemas';
import { useToast } from '@/hooks/use-toast';
import { categoryLabelFor, categorySortOrder } from '@/lib/category-labels';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { MaskedDateInput } from '@/components/forms/MaskedDateInput';
import { Button } from '@/components/ui/button';
import { reviewParent, setUploadsOrigin, UPLOADS_SECTION } from '@/lib/uploadNav';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { SummaryBarChart } from '@/components/dashboard/SummaryBarChart';
import { EditMarkerSheet } from '@/components/uploads/EditMarkerSheet';
import { ViewBlankButton } from '@/components/uploads/ViewBlankButton';
import type {
  ConfirmUploadRequest,
  EnrichedBiomarker,
  ReviewEditItem,
  ReviewWarning,
  GetUploadResponse,
  PatientNameMatch,
  RawAiUnrecognizedRow,
  Upload,
  UploadApiError,
} from '@/api/types';

export function HomePending({ uploadId }: { uploadId: number }) {
  const { data, error, isLoading, isError } = useUpload(uploadId);
  const [, setLocation] = useLocation();

  if (isLoading) {
    return (
      <MobileFrame>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileFrame>
    );
  }
  // Defensive: data.upload может быть undefined если в react-query cache лежит
  // устаревший shape (после смены useUpload с Upload на GetUploadResponse в этой сессии).
  if (isError || !data || !data.upload || !data.upload.raw_ai_response) {
    // HEAL-81: у этого экрана свой адрес, и по нему тоже ходят по чужим ссылкам.
    //
    // Правка на экране анализа сама по себе дыру не закрывала: `/uploads/{id}/review`
    // открывается напрямую ровно так же, а говорил он то же самое «не удалось
    // загрузить» на отказ в доступе. Текст тот же, что и там, — намеренно: две
    // формулировки для одного случая человек читает как две разные причины.
    const notFound = error?.response?.status === 404;

    return (
      <MobileFrame>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {notFound
              ? 'Анализ не найден или у вас нет доступа.'
              : 'Не удалось загрузить распознанные данные.'}
          </p>
          {/* HEAL-46a: выход обязателен. Раньше экран жил на Главной, и уйти можно
              было нижним меню; теперь у него свой адрес, и без кнопки человек
              заперт на сообщении об ошибке. */}
          <Button variant="outline" onClick={() => setLocation('/')}>
            На Главную
          </Button>
        </div>
      </MobileFrame>
    );
  }
  return <HomePendingReady response={data} />;
}

function HomePendingReady({ response }: { response: GetUploadResponse }) {
  const upload = response.upload;
  const ai = upload.raw_ai_response!;
  const enriched = response.enriched_biomarkers ?? [];
  const summary = response.summary ?? null;
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const showUploadError = useUploadErrorToast();
  const confirmMutation = useConfirmUpload(upload.id);
  const reportMutation = useReportUpload(upload.id);

  const hasAiDate = upload.measured_at !== null;
  const [localDate, setLocalDate] = useState<string>('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [unrecognizedOpen, setUnrecognizedOpen] = useState(false);

  const localDateError = useMemo(() => {
    if (hasAiDate || !localDate) return null;
    const parse = ConfirmUploadSchema.shape.measured_at.unwrap().safeParse(localDate);
    if (!parse.success) return parse.error.issues[0]?.message ?? 'Дата выглядит неправильно';
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setUTCFullYear(fiveYearsAgo.getUTCFullYear() - 5);
    if (new Date(localDate + 'T00:00:00Z') < fiveYearsAgo) {
      return 'Дата слишком старая (более 5 лет назад)';
    }
    return null;
  }, [hasAiDate, localDate]);

  const confirmDisabled = (!hasAiDate && (!localDate || !!localDateError)) || confirmMutation.isPending;

  // HEAL-44b — режим правки. Ответ сервера единственный источник истины для
  // цветов и сводки: если считать их на клиенте, цвет на экране правки разойдётся
  // с цветом после подтверждения.
  const reviewMutation = useUpdateReview(upload.id);
  const [editing, setEditing] = useState<EnrichedBiomarker | null>(null);

  // ⚠️ Стартуем с ТЕХ правок, что уже лежат на сервере, а не с пустого набора.
  //
  // PATCH /review трактует запрос как полный снимок и заменяет user_edits целиком.
  // Клиент, начинающий с нуля, отправлял бы снимок из одной последней правки — и
  // все прежние молча исчезали. Ловится это только после перезапуска приложения
  // или повторного входа на экран, поэтому и не попадалось на глаза.
  const [edits, setEdits] = useState<{ removed: number[]; items: ReviewEditItem[] }>(() => ({
    removed: upload.user_edits?.removed ?? [],
    items: upload.user_edits?.items ?? [],
  }));
  const [warnings44b, setWarnings44b] = useState<ReviewWarning[]>([]);

  const sendEdits = async (next: { removed: number[]; items: ReviewEditItem[] }) => {
    try {
      const result = await reviewMutation.mutateAsync(next);
      setEdits(next);
      setWarnings44b(result.warnings);
      return result;
    } catch (e) {
      showUploadError(e as AxiosError<UploadApiError>);
      return null;
    }
  };

  const handleSaveEdit = async (edit: ReviewEditItem) => {
    // Правки одной строки НАКАПЛИВАЮТСЯ: человек мог сначала поправить значение,
    // а потом вернуться и сменить единицу. Замена целиком стёрла бы первое.
    const previous = edits.items.find((i) => i.source_index === edit.source_index);
    const merged = { ...previous, ...edit };

    const next = {
      removed: edits.removed,
      items: [...edits.items.filter((i) => i.source_index !== edit.source_index), merged],
    };

    const result = await sendEdits(next);
    if (result === null) return;

    // Лист закрываем, только если по этой строке нет предупреждения: иначе
    // человек ввёл «220» вместо «2,20», нажал «Готово» и увидел бы основной
    // экран без единого намёка на проблему — предупреждение живёт внутри листа.
    const warning = result.warnings.some((w) => w.source_index === edit.source_index);

    if (warning) {
      // Оставляем открытым, но на ОБНОВЛЁННОЙ строке: иначе поля покажут
      // доправочное состояние, и человек не поймёт, к чему относится текст.
      const refreshed = result.enriched_biomarkers.find(
        (m) => m.source_index === edit.source_index,
      );
      if (refreshed) setEditing(refreshed);
      return;
    }

    setEditing(null);
  };

  const handleRemoveRow = async (sourceIndex: number) => {
    const next = {
      removed: [...edits.removed.filter((i) => i !== sourceIndex), sourceIndex],
      items: edits.items.filter((i) => i.source_index !== sourceIndex),
    };
    if (await sendEdits(next)) setEditing(null);
  };

  const handleResetEdits = async () => {
    await sendEdits({ removed: [], items: [] });
  };

  const hasEdits = edits.removed.length > 0 || edits.items.length > 0;

  const grouped = useMemo(() => groupByCategory(enriched), [enriched]);
  const warnings = useMemo(() => visibleWarnings(ai.warnings), [ai.warnings]);
  const unrecognized = useMemo(
    () => visibleUnrecognized(ai.unrecognized_rows),
    [ai.unrecognized_rows],
  );

  const handleConfirm = async () => {
    if (confirmDisabled) return;
    const body: ConfirmUploadRequest = hasAiDate ? {} : { measured_at: localDate };
    try {
      await confirmMutation.mutateAsync(body);
      sessionStorage.setItem(
        'healthlab:just_uploaded',
        JSON.stringify({ count: ai.biomarkers.length, lab: upload.lab_source }),
      );
      // HEAL-46a D-17 + HEAL-82: возврат к родителю. Файл из пакета — в пакет,
      // оттуда человек идёт к следующему готовому в порядке своего выбора.
      // Разбирал из «Моих загрузок» — вернётся туда же, а не вылетит на Главную.
      setLocation(reviewParent(upload.batch_id));
    } catch (e) {
      showUploadError(e as AxiosError<UploadApiError>);
    }
  };

  const handleLeave = () => setLocation(reviewParent(upload.batch_id));

  const reportParse = ReportUploadSchema.safeParse({ comment: reportText });
  const reportDisabled = !reportParse.success || reportMutation.isPending;

  const handleReport = async () => {
    if (reportDisabled) return;
    try {
      await reportMutation.mutateAsync({ comment: reportText.trim() || undefined });
      toast({ title: 'Спасибо, разберёмся', description: 'Мы посмотрим этот файл в ближайшее время.', variant: 'success' });
      setReportOpen(false);
      setReportText('');
      // Объявляем раздел (круг ревью, находка 5): иначе «Назад» из истории
      // увёл бы человека в Профиль, где он не был, — по признаку от прошлого
      // потока.
      setUploadsOrigin('home');
      setLocation(UPLOADS_SECTION);
    } catch (e) {
      showUploadError(e as AxiosError<UploadApiError>);
    }
  };

  return (
    <MobileFrame className="pb-[calc(8rem_+_env(safe-area-inset-bottom))]">
      <div className="flex h-12 flex-shrink-0 items-center px-4">
        <div className="text-base font-semibold">Проверьте и сохраните</div>
      </div>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4 pt-3">
        <ReviewBanner
          upload={upload}
          biomarkerCount={ai.biomarkers.length}
          hasAiDate={hasAiDate}
          localDate={localDate}
          setLocalDate={setLocalDate}
          localDateError={localDateError}
        />

        {/* Сразу под баннером (а не в конце скролла) — на длинном анализе юзер
            мог бы подтвердить, не долистав до плашки и не увидев предупреждение. */}
        {shouldShowNameMismatchNotice(upload.patient_name_match) && <NameMismatchNotice />}

        {warnings.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {warnings.map((w, idx) => (
              <span
                key={idx}
                className="rounded-full bg-amber-100 px-2 py-0.5 text-hl-small text-amber-900"
              >
                {w}
              </span>
            ))}
          </div>
        )}

        {summary && summary.total > 0 && (
          <SummaryBarChart
            total={summary.total}
            normal={summary.normal}
            attention={summary.attention}
            outOfRange={summary.out_of_range}
            unknown={summary.unknown}
            totalUnit={pluralize(summary.total, ['показатель', 'показателя', 'показателей'])}
          />
        )}

        {grouped.length > 0 && (
          <div className="flex flex-col gap-5">
            <div className="border-b border-border pb-2 text-base font-bold text-foreground">
              Показатели
            </div>
            {grouped.map(({ category, items }) => (
              <CategoryBlock
                key={category}
                category={category}
                items={items}
                onEdit={setEditing}
              />
            ))}

            {/* HEAL-44b: сброс — это жест «я передумал», он должен быть рядом с
                правками, а не в глубине настроек. */}
            {hasEdits && (
              <button
                type="button"
                onClick={handleResetEdits}
                disabled={reviewMutation.isPending}
                className="self-start text-hl-secondary text-primary-text disabled:opacity-50"
              >
                Вернуть всё как распознано
              </button>
            )}
          </div>
        )}

        {/* HEAL-44a, пункт 9 тикета: бланк доступен и ДО подтверждения. Именно это
            делает правку осмысленной — иначе человек правит 5 на 4 по памяти. */}
        <ViewBlankButton
          uploadId={upload.id}
          fileSize={upload.file_size}
          mimeType={upload.mime_type}
          className="w-full"
        />

        {unrecognized.length > 0 && (
          <div className="rounded-surface border border-border bg-card">
            <button
              type="button"
              onClick={() => setUnrecognizedOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium"
            >
              <span>
                Не удалось распознать{' '}
                <span className="text-muted-foreground">
                  ({unrecognized.length}{' '}
                  {pluralize(unrecognized.length, ['показатель', 'показателя', 'показателей'])})
                </span>
              </span>
              {unrecognizedOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            {unrecognizedOpen && (
              <div className="border-t border-border px-4 py-3">
                <ul className="flex flex-col gap-2 text-xs">
                  {unrecognized.map((row, idx) => (
                    <UnrecognizedRow key={idx} rawText={row.raw_text} label={row.label} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* fixed → escape'ит MobileFrame, поэтому safe-area-inset-bottom добавляем здесь сами,
          иначе CTA «срезается» у home-indicator. */}
      <div className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-md border-t border-border bg-background/95 px-4 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))] backdrop-blur">
        <Button onClick={handleConfirm} disabled={confirmDisabled} className="w-full">
          {confirmMutation.isPending ? 'Сохраняем…' : 'Подтвердить и сохранить'}
        </Button>
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          className="mt-2 block w-full text-center text-sm text-primary-text"
        >
          Сообщить о проблеме →
        </button>
        {/* HEAL-46a D-18 — выход. До этой версии его не было вовсе: с экрана
            проверки можно было только подтвердить или пожаловаться, а Главная
            подменялась этим же экраном. При пакете из двух десятков файлов это
            ловушка — человек заперт до конца разбора.

            Признак ухода переживает перезапуск приложения: иначе выход работал бы
            до первого закрытия, то есть не работал бы. */}
        <button
          type="button"
          onClick={handleLeave}
          className="mt-1 block w-full py-1 text-center text-sm text-muted-foreground"
        >
          Вернусь позже
        </button>
      </div>

      <EditMarkerSheet
        marker={editing}
        warning={
          editing?.source_index === undefined
            ? null
            : (warnings44b.find((w) => w.source_index === editing.source_index) ?? null)
        }
        pending={reviewMutation.isPending}
        onClose={() => setEditing(null)}
        onSave={handleSaveEdit}
        onRemove={handleRemoveRow}
      />

      <BottomSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        ariaLabel="Сообщить о проблеме"
      >
        <div className="p-6">
          <h2 className="mb-1 text-lg font-semibold">Что не так с распознаванием?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Коротко опишите проблему — мы посмотрим этот файл вручную.
          </p>
          <textarea
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            maxLength={500}
            rows={5}
            placeholder="Например: дата неверная, или половина значений не распозналась…"
            className="w-full rounded-md border border-border bg-background p-3 text-sm"
          />
          <div className="mt-1 text-right text-hl-small text-muted-foreground">
            {reportText.length} / 500
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <Button onClick={handleReport} disabled={reportDisabled} className="w-full">
              {reportMutation.isPending ? 'Отправляем…' : 'Отправить'}
            </Button>
            <button
              type="button"
              onClick={() => setReportOpen(false)}
              className="w-full py-2 text-sm font-medium text-muted-foreground"
            >
              Отмена
            </button>
          </div>
        </div>
      </BottomSheet>
    </MobileFrame>
  );
}

// Mini-sprint 6-identity — мягкую плашку показываем ТОЛЬКО при вердикте `mismatch`.
// match / unknown / null → плашки нет (Stage 1 §2). Действия не блокируются (D6).
export function shouldShowNameMismatchNotice(verdict: PatientNameMatch): boolean {
  return verdict === 'mismatch';
}

// Mini-sprint 6-identity — мягкая плашка при несовпадении фамилии владельца
// аккаунта с фамилией пациента на бланке (вердикт `mismatch`). Только информирует:
// действия подтверждения/отказа не меняются и не блокируются (D6, C2).
function NameMismatchNotice() {
  return (
    <div
      data-testid="name-mismatch-notice"
      className="flex items-start gap-3 rounded-surface border border-amber-300 bg-amber-50 p-4"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <p className="text-sm leading-snug text-amber-900">
        ФИО на бланке не совпадает с указанным в профиле. Это точно ваш анализ?
      </p>
    </div>
  );
}

function ReviewBanner({
  upload,
  biomarkerCount,
  hasAiDate,
  localDate,
  setLocalDate,
  localDateError,
}: {
  upload: Upload;
  biomarkerCount: number;
  hasAiDate: boolean;
  localDate: string;
  setLocalDate: (v: string) => void;
  localDateError: string | null;
}) {
  return (
    <div className="rounded-surface border border-amber-200 bg-amber-50 p-4">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-control bg-amber-200/60 text-amber-900">
          <ClipboardCheck className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-amber-900">
            Распознали {biomarkerCount}{' '}
            {pluralize(biomarkerCount, ['показатель', 'показателя', 'показателей'])}
          </div>
          <div className="break-words text-xs text-amber-800/80">
            {upload.lab_source ?? 'Источник неизвестен'} · {upload.original_filename}
          </div>
        </div>
      </div>

      {hasAiDate ? (
        <div className="flex items-center gap-3 px-1">
          <Calendar className="h-4 w-4 flex-shrink-0 text-amber-700" aria-hidden="true" />
          <div className="flex-1">
            <div className="text-hl-small text-amber-800/80">Дата сдачи</div>
            <div className="text-sm font-semibold text-amber-900">
              {formatRu(upload.measured_at!)}
            </div>
          </div>
        </div>
      ) : (
        <label className="block rounded-surface bg-surface p-3">
          <div className="mb-1 flex items-center gap-3">
            <Calendar className="h-4 w-4 flex-shrink-0 text-amber-700" aria-hidden="true" />
            <div className="text-xs font-medium text-gray-700">Когда вы сдавали этот анализ?</div>
          </div>
          <MaskedDateInput
            value={localDate}
            onChange={setLocalDate}
            className="w-full rounded-md border border-amber-300 bg-surface px-3 py-2 text-sm text-foreground"
          />
          {localDateError && (
            <div className="mt-1 text-hl-small font-medium text-rose-700">{localDateError}</div>
          )}
        </label>
      )}

      <p className="mt-2 text-hl-small leading-snug text-amber-800/80">
        {hasAiDate
          ? 'AI нашёл дату в файле. Если она неверна — нажмите «Сообщить о проблеме».'
          : 'AI не нашёл дату в файле — укажите вручную.'}
      </p>
    </div>
  );
}

function CategoryBlock({
  category,
  items,
  onEdit,
}: {
  category: string;
  items: EnrichedBiomarker[];
  onEdit: (marker: EnrichedBiomarker) => void;
}) {
  return (
    <div>
      <div className="mb-2 text-hl-small font-semibold uppercase tracking-wider text-muted-foreground">
        {category === '__other__' ? 'Прочее' : categoryLabelFor(category)}
      </div>
      <div className="flex flex-col gap-2">
        {items.map((b, idx) => (
          <EnrichedBiomarkerCard
            key={`${b.code ?? 'unmatched'}-${idx}`}
            marker={b}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
}

function EnrichedBiomarkerCard({
  marker,
  onEdit,
}: {
  marker: EnrichedBiomarker;
  onEdit: (marker: EnrichedBiomarker) => void;
}) {
  // HEAL-22: цвет карточки берётся из ЕДИНОГО резолвера (lib/biomarkerStatus), того же,
  // что красит Главную/органы/категории/деталь. Локального маппинга здесь больше нет —
  // именно он красил «вне нормы» оранжевым до сохранения и красным после.
  const tier = reviewDisplayTier(marker.status, marker.optimum_status);
  const borderClass = `border-l-4 ${BORDER_LEFT_BY_TIER[tier]}`;

  const rangeText = marker.matched_range
    ? `Норма: ${formatRange(marker.matched_range)}${marker.unit ? ' ' + marker.unit : ''}`
    : marker.reference_range_raw
      ? `Норма по бланку: ${marker.reference_range_raw}`
      : null;

  // HEAL-33: качественный ответ показываем словом. `marker.value` у таких
  // показателей — порядковая оценка (0/1), пользователю она ничего не говорит.
  const valueText = marker.value_qualitative
    ? capitalizeFirst(marker.value_qualitative)
    : marker.value !== null
      ? `${formatNum(marker.value)}${marker.unit ? ' ' + marker.unit : ''}`
      : '—';

  // Строка без source_index править нельзя — это анализ, загруженный до HEAL-44b.
  const editable = marker.source_index !== undefined;

  return (
    <button
      type="button"
      onClick={() => editable && onEdit(marker)}
      disabled={!editable}
      className={`w-full rounded-surface bg-surface p-3 text-left shadow-sm transition-colors disabled:cursor-default ${borderClass} ${editable ? 'active:bg-muted' : ''}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <div className="truncate text-sm font-semibold">{capitalizeFirst(marker.name_ru)}</div>
          {marker.unmatched && (
            <AlertCircle
              className="h-3.5 w-3.5 flex-shrink-0 text-gray-400"
              aria-label="Не найден в справочнике"
            />
          )}
        </div>
        <div className="flex-shrink-0 font-mono text-sm font-semibold">{valueText}</div>
      </div>
      {rangeText && <div className="mt-1 text-xs text-muted-foreground">{rangeText}</div>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {marker.low_confidence && (
          <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-hl-small text-slate-600">
            AI не уверен
          </span>
        )}
        {/* Человек должен видеть, где он вмешался: это и защита от случайной
            правки, и доверие к тому, что мы ничего не поменяли сами. */}
        {marker.edited_by_user && (
          <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-hl-small text-primary-text">
            Изменено
          </span>
        )}
      </div>
    </button>
  );
}

function UnrecognizedRow({ rawText, label }: { rawText: string; label: string }) {
  return (
    <li className="rounded-md bg-muted/40 p-2">
      <div className="text-hl-small font-medium text-foreground">{capitalizeFirst(rawText)}</div>
      <div className="mt-1 text-hl-small text-muted-foreground">{label}</div>
    </li>
  );
}

function groupByCategory(items: EnrichedBiomarker[]): Array<{ category: string; items: EnrichedBiomarker[] }> {
  const map = new Map<string, EnrichedBiomarker[]>();
  for (const item of items) {
    const key = item.category ?? '__other__';
    const arr = map.get(key) ?? [];
    arr.push(item);
    map.set(key, arr);
  }
  return Array.from(map.entries())
    .map(([category, list]) => ({ category, items: sortByAttentionFirst(list) }))
    .sort((a, b) => {
      if (a.category === '__other__') return 1;
      if (b.category === '__other__') return -1;
      return categorySortOrder(a.category) - categorySortOrder(b.category);
    });
}

/**
 * Двухуровневая сортировка внутри категории:
 *   1) приоритет дисплей-тира — 🔴 вне нормы вверх, затем 🟠, 🟢, без оценки
 *   2) внутри каждого тира — стабильный исходный порядок (он клинический,
 *      приходит из справочника biomarkers.sort_order через backend)
 *
 * HEAL-22: ранжирование делегировано общему `sortByTierWith` — тот же порядок,
 * что на Главной и в «Маркерах» (здесь был локальный дубль `statusPriority`).
 */
function sortByAttentionFirst(items: EnrichedBiomarker[]): EnrichedBiomarker[] {
  return sortByTierWith(items, (item) => reviewDisplayTier(item.status, item.optimum_status));
}

const WARNING_LABELS: Record<string, string> = {
  multiple_dates_found_picked_earliest: 'В файле несколько дат — выбрана самая ранняя',
};

/**
 * HEAL-31 — аллоулист, а не блоклист. Раньше здесь пряталось только то, что
 * похоже на технический код (`^[a-z0-9_]+$`), а свободная английская проза
 * модели считалась «человекочитаемой» и показывалась дословно — именно так на
 * экран попадало «Some text was masked and could not be fully recognized».
 * Показываем только те коды, которые мы сами знаем и умеем перевести.
 */
export function visibleWarnings(warnings: string[]): string[] {
  return warnings
    .map((warning) => WARNING_LABELS[warning] ?? null)
    .filter((label): label is string => label !== null);
}

/**
 * HEAL-31 — то же правило для блока «Не удалось распознать». Бэкенд нормализует
 * `reason` в закрытый набор кодов; здесь остаётся единственный, у которого есть
 * смысл для пользователя: имя показателя распозналось, а значение — нет.
 * `masked_text` / `other` — технический шум бланка, показывать нечего.
 */
const UNRECOGNIZED_LABELS: Record<string, string> = {
  value_missing: 'Не удалось прочитать значение',
};

export function visibleUnrecognized(
  rows: RawAiUnrecognizedRow[],
): Array<{ raw_text: string; label: string }> {
  return rows.flatMap((row) => {
    const label = row.reason === null ? null : (UNRECOGNIZED_LABELS[row.reason] ?? null);
    if (label === null || row.raw_text.trim() === '') return [];
    return [{ raw_text: row.raw_text, label }];
  });
}

function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

function formatNum(n: number): string {
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/**
 * HEAL-86: односторонняя норма — пустая граница («от 10» у анти-HBs, «до 3»).
 * Раньше пустая граница шла в toLocaleString и роняла экран проверки.
 */
export function formatRange(range: { min: number | null; max: number | null }): string {
  if (range.min !== null && range.max === null) return `от ${formatNum(range.min)}`;
  if (range.min === null && range.max !== null) return `до ${formatNum(range.max)}`;
  if (range.min === null || range.max === null) return '—';
  return `${formatNum(range.min)} – ${formatNum(range.max)}`;
}

function formatRu(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
